import { tsv } from "@faire/slate/lib/tsv";
import React from "react";

import { cn } from "../../lib/utils";

/**
 * Retailer global top navigation (current production).
 *
 * The Faire marketplace header: logo, "All categories", search pill,
 * notification / account / cart (with count badge), and the category row.
 * Sticky to the top of the viewport. Faithfully ported from production markup.
 */

export interface RetailerTopNavProps {
  cartCount?: number;
  searchPlaceholder?: string;
  categories?: string[];
  /** Category rendered in the sale color (defaults to "Sale"). */
  saleCategory?: string;
  /**
   * Replaces the inert search pill. Used when a prototype owns search itself —
   * a typeahead overlay, a live input — rather than the decorative field.
   */
  searchField?: React.ReactNode;
  /**
   * Called when the Faire logo is clicked. When provided the logo becomes an
   * interactive "go home" affordance, matching production. Omitted by default,
   * so existing prototypes render an inert logo exactly as before.
   */
  onLogoClick?: () => void;
  /**
   * Called when the cart icon is clicked. When provided the icon becomes a
   * button; omitted by default, so existing prototypes render it inert.
   */
  onCartClick?: () => void;
  /** Locale chip shown beside the notification bell (e.g. "EN-US"). */
  localeLabel?: string;
  /**
   * Items for the account dropdown, in production's order. When omitted the
   * account glyph stays inert, exactly as existing prototypes render it.
   */
  accountMenuItems?: AccountMenuItem[];
}

export interface AccountMenuItem {
  label: string;
  /** Draws a section rule above this item, as production's menu does. */
  startsSection?: boolean;
  onClick?: () => void;
}

const DEFAULT_CATEGORIES = [
  "Bestsellers",
  "Faire Source",
  "Trending",
  "Local",
  "New products",
  "New brands",
  "Gifts",
  "Home decor",
  "Beauty & wellness",
  "Paper & novelty",
  "Summer Shop",
  "Sale",
];

const RetailerTopNav: React.FC<RetailerTopNavProps> = ({
  cartCount = 6,
  searchPlaceholder = "Search products or brands",
  categories = DEFAULT_CATEGORIES,
  saleCategory = "Sale",
  onLogoClick,
  onCartClick,
  localeLabel,
  accountMenuItems,
  searchField,
}) => (
  <header className="bg-fs-surface-primary border-fs-border-muted sticky top-0 z-50 w-full border-b">
    <div className="mx-auto flex w-full max-w-[1920px] flex-wrap items-center gap-x-2 gap-y-3 px-4 pt-4 pb-3 md:flex-nowrap md:gap-4 md:px-12 md:pb-2">
      <span aria-hidden="true" className="flex size-10 shrink-0 items-center justify-center md:hidden">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M3 6h18M3 12h18M3 18h18" />
        </svg>
      </span>
      {onLogoClick ? (
        <button
          type="button"
          onClick={onLogoClick}
          aria-label="Faire home"
          className="shrink-0"
        >
          <img
            alt="Faire"
            className="h-[18px] w-[104px] shrink-0"
            src="https://cdn.faire.com/static/logo.svg"
          />
        </button>
      ) : (
        <img
          alt="Faire"
          className="h-[18px] w-[104px] shrink-0"
          src="https://cdn.faire.com/static/logo.svg"
        />
      )}
      <button className="text-fs-text-primary type-fs-paragraph ml-6 hidden shrink-0 items-center gap-2 md:flex">
        All categories
        <NavChevronDown />
      </button>
      <div className="order-last flex w-full min-w-0 *:min-w-0 *:flex-1 md:order-none md:w-auto md:flex-1">
      {searchField ?? (
        <div className="border-fs-border-muted text-fs-text-subdued flex h-10 min-w-0 flex-1 items-center gap-2 rounded-full border px-4">
          <NavSearchIcon />
          <span className="type-fs-paragraph text-fs-text-subdued truncate">
            {searchPlaceholder}
          </span>
        </div>
      )}
      </div>
      <div className="ml-auto flex shrink-0 items-center gap-2 md:ml-0">
        {localeLabel ? (
          <span className="type-fs-paragraph text-fs-text-primary mr-4 hidden items-center gap-2 md:flex">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18" />
            </svg>
            {localeLabel}
          </span>
        ) : null}
        <span className="hidden size-10 items-center justify-center md:flex">
          <NavBellIcon />
        </span>
        {accountMenuItems ? (
          <AccountMenu items={accountMenuItems} />
        ) : (
          <span className="flex size-10 items-center justify-center">
            <NavAccountIcon />
          </span>
        )}
        {onCartClick ? (
          <button
            type="button"
            aria-label="Cart"
            onClick={onCartClick}
            className="relative flex size-10 items-center justify-center"
          >
            <NavCartIcon />
            <CartBadge count={cartCount} />
          </button>
        ) : (
          <span className="relative flex size-10 items-center justify-center">
            <NavCartIcon />
            <CartBadge count={cartCount} />
          </span>
        )}
      </div>
    </div>
    <nav className="hidden overflow-x-auto px-4 pt-[10px] pb-2 md:flex md:flex-wrap md:justify-center md:px-6">
      {categories.map((label) => (
        <a
          key={label}
          href="#"
          className={cn(
            "type-fs-paragraph whitespace-nowrap px-[7px] py-[4px]",
            label === saleCategory
              ? "text-fs-message-text-sale"
              : "text-fs-text-primary"
          )}
        >
          {label}
        </a>
      ))}
    </nav>
  </header>
);

/**
 * Account dropdown, following production's menu: a single list of destinations
 * with section rules, opened by the account glyph and dismissed by clicking
 * away or pressing Escape.
 */
const AccountMenu: React.FC<{ items: AccountMenuItem[] }> = ({ items }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative flex">
      <button
        type="button"
        aria-label="Account"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        onClick={() => setIsOpen((open) => !open)}
        className="hover:bg-fs-action-surface-inverse-hover flex size-10 items-center justify-center rounded-full transition-colors"
      >
        <NavAccountIcon />
      </button>
      {isOpen ? (
        <div role="menu" className={accountMenuStyle.cls()}>
          {items.map((item) => (
            <React.Fragment key={item.label}>
              {item.startsSection ? (
                <span className="bg-fs-border-muted my-2 block h-px w-full" />
              ) : null}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setIsOpen(false);
                  item.onClick?.();
                }}
                className={accountMenuItemStyle.cls()}
              >
                {item.label}
              </button>
            </React.Fragment>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const accountMenuStyle = tsv({
  base: [
    "bg-fs-surface-primary border-fs-border-muted",
    "absolute top-11 right-0 z-50 flex w-[248px] flex-col rounded-lg border py-2",
    "shadow-[0_8px_28px_rgba(0,0,0,0.18)]",
  ],
});

const accountMenuItemStyle = tsv({
  base: [
    "type-fs-paragraph text-fs-text-primary hover:bg-fs-action-surface-inverse-hover",
    "w-full px-4 py-2 text-left transition-colors",
  ],
});

export default RetailerTopNav;

const NavChevronDown: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    className="size-3"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const NavSearchIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    className="size-4 shrink-0"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M16.71 16.71A9.287 9.287 0 1 1 3.577 3.576 9.287 9.287 0 0 1 16.71 16.71Zm0 0 6.433 6.433" />
  </svg>
);

const NavBellIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    className="size-5 shrink-0"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9.021 20.045a2.71 2.71 0 0 0 2.622 2.028 2.71 2.71 0 0 0 2.621-2.028M12 1.927c1.864 0 3.652.769 4.97 2.136A7.43 7.43 0 0 1 19.03 9.22c0 8.11 2.85 8.327 3.621 8.327H1.35c.786 0 3.621-.235 3.621-8.327 0-1.934.74-3.79 2.059-5.157A6.9 6.9 0 0 1 12 1.927Z" />
  </svg>
);

const NavAccountIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    className="size-5 shrink-0"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M8 7.21a4 4 0 1 0 8 0 4 4 0 0 0-8 0ZM21 20.79c-.617-1.744-1.82-3.264-3.437-4.341S14 14.79 12 14.79s-3.947.58-5.563 1.659C4.82 17.526 3.617 19.046 3 20.79z" />
  </svg>
);

const CartBadge: React.FC<{ count: number }> = ({ count }) =>
  count > 0 ? (
    <span
      data-testid="cart-badge"
      className="absolute -top-0.5 -right-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[11px] leading-none font-medium"
      style={{ backgroundColor: "#333333", color: "#ffffff" }}
    >
      {count}
    </span>
  ) : null;

const NavCartIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    className="size-5 shrink-0"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20.74 14.714H7.744L6.029 5.286h16.114a.857.857 0 0 1 .843 1.01l-1.402 7.714a.86.86 0 0 1-.843.704ZM6.029 5.286l-.72-3.6A.86.86 0 0 0 4.469 1H1.743M7.743 14.714l.72 3.6a.86.86 0 0 0 .84.686h9.583" />
    <path d="M18.884 22.429a.857.857 0 1 1-1.714 0 .857.857 0 0 1 1.714 0ZM11.17 22.429a.857.857 0 1 1-1.715 0 .857.857 0 0 1 1.715 0Z" fill="currentColor" />
  </svg>
);
